/**
 * TeamInsightsTab — shows reportee cards and their email insights.
 * Manager clicks "View Insights" → runs analysis through the backend Gmail proxy.
 * No Google token is ever handed to the manager's browser.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Button, Avatar, Tag, Alert, Typography, Row, Col, Spin, Tooltip, Skeleton, Pagination, Input } from 'antd';
import { Users, Mail, RefreshCw, MailCheck, MailX, Search, MailWarning } from 'lucide-react';
import { useEmailFetch } from '../hooks/useEmailFetch';
import { useMetrics } from '../hooks/useMetrics';
import { ControlsBar } from './ControlsBar';
import { FetchProgress } from './FetchProgress';
import { ReadyToAnalyze } from './ReadyToAnalyze';
import { StatsRow } from './StatsRow';
import { Charts } from './Charts';
import { HeatmapChart } from './HeatmapChart';
import { DayOfWeekChart } from './DayOfWeekChart';
import { ContactTables } from './ContactTables';
import { ResponseRatePanel } from './ResponseRatePanel';
import { EmailDebtPanel } from './EmailDebtPanel';
import { DelayedEmailsPanel } from './DelayedEmailsPanel';
import { CommunicationLoopsPanel } from './CommunicationLoopsPanel';
import { DEFAULT_DELAY_THRESHOLD, DEFAULT_DAYS } from '../constants';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

function avatarColor(email = '') {
  const colors = ['#6366f1', '#22c55e', '#f59e0b', '#3b82f6', '#ef4444', '#8b5cf6', '#ec4899'];
  let hash = 0;
  for (const c of email) hash = c.charCodeAt(0) + hash * 31;
  return colors[Math.abs(hash) % colors.length];
}

function getInitials(name = '') {
  return name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);
}

/** Single reportee card in the grid */
function MemberCard({ member, onViewInsights, loading, isDark }) {
  const acColor = avatarColor(member.email);
  return (
    <div
      style={{
        borderRadius: 14,
        background: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0'}`,
        boxShadow: isDark
          ? '0 1px 3px rgba(0,0,0,0.2)'
          : '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
        padding: 20,
        width: '100%',
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = isDark
          ? '0 4px 12px rgba(0,0,0,0.3)'
          : '0 4px 12px rgba(0,0,0,0.08)';
        e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = isDark
          ? '0 1px 3px rgba(0,0,0,0.2)'
          : '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)';
        e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <Avatar
          size={40}
          style={{
            background: `linear-gradient(135deg, ${acColor}, ${acColor}cc)`,
            fontWeight: 700,
            fontSize: 14,
            flexShrink: 0,
            boxShadow: `0 2px 6px ${acColor}25`,
          }}
        >
          {getInitials(member.name || member.email)}
        </Avatar>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text strong style={{ display: 'block', fontSize: 13 }} ellipsis>{member.name || member.email}</Text>
          <Text type="secondary" style={{ fontSize: 11.5 }} ellipsis>{member.email}</Text>
          {member.role && <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>{member.role}</Text>}
        </div>
        {member.tokenExpired ? (
          <Tooltip title="Gmail connection expired — ask member to reconnect">
            <div style={{
              width: 28, height: 28, borderRadius: 8,
              background: isDark ? 'rgba(245,158,11,0.15)' : 'rgba(245,158,11,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <MailWarning size={14} color="#f59e0b" strokeWidth={2.2} />
            </div>
          </Tooltip>
        ) : member.gmailConnected ? (
          <Tooltip title="Gmail connected">
            <div style={{
              width: 28, height: 28, borderRadius: 8,
              background: isDark ? 'rgba(16,185,129,0.12)' : 'rgba(16,185,129,0.08)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <MailCheck size={14} color="#10b981" strokeWidth={2.2} />
            </div>
          </Tooltip>
        ) : (
          <Tooltip title="Gmail not connected">
            <div style={{
              width: 28, height: 28, borderRadius: 8,
              background: isDark ? 'rgba(148,163,184,0.08)' : 'rgba(148,163,184,0.05)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <MailX size={14} color="#94a3b8" strokeWidth={2.2} />
            </div>
          </Tooltip>
        )}
      </div>

      {member.tokenExpired ? (
        <Tooltip title="Ask this member to reconnect their Gmail">
          <Tag color="orange" style={{ width: '100%', textAlign: 'center', padding: '4px 0', cursor: 'default', borderRadius: 8 }}>
            Token Expired — Ask to Reconnect
          </Tag>
        </Tooltip>
      ) : member.gmailConnected ? (
        <Button
          type="primary"
          size="small"
          block
          loading={loading}
          onClick={() => onViewInsights(member)}
          icon={<Mail size={13} />}
          style={{
            borderRadius: 8,
            fontWeight: 600,
            height: 32,
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            border: 'none',
            boxShadow: '0 2px 6px rgba(99,102,241,0.2)',
          }}
        >
          View Insights
        </Button>
      ) : null}
    </div>
  );
}

/** Full insights view for a selected member */
function MemberInsightsView({ member, emailFetch, isDark, onBack, onAnalyze, connectionExpired }) {
  const metrics = useMetrics(emailFetch.data);
  const [delayThreshold, setDelayThreshold] = useState(DEFAULT_DELAY_THRESHOLD);
  const [delayedModalOpen, setDelayedModalOpen] = useState(false);
  const [debtModalOpen, setDebtModalOpen] = useState(false);
  const [loopsModalOpen, setLoopsModalOpen] = useState(false);
  const rangeRef = useRef([dayjs().subtract(DEFAULT_DAYS, 'day'), dayjs()]);
  const [rangeDays, setRangeDays] = useState(DEFAULT_DAYS);

  const accentColor = avatarColor(member.email);
  const expired = connectionExpired || member.tokenExpired;

  return (
    <div>
      {/* Member header bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        marginBottom: 20,
        padding: '14px 20px',
        borderRadius: 14,
        background: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0'}`,
        boxShadow: isDark
          ? '0 1px 3px rgba(0,0,0,0.2)'
          : '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
      }}>
        <Button
          size="small"
          onClick={onBack}
          style={{
            borderRadius: 8,
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '4px 12px',
            height: 32,
          }}
        >
          ← Back
        </Button>

        <div style={{
          width: 1,
          height: 28,
          background: isDark ? 'rgba(255,255,255,0.08)' : '#e5e7eb',
          flexShrink: 0,
        }} />

        <Avatar
          size={36}
          style={{
            background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`,
            fontWeight: 700,
            fontSize: 14,
            flexShrink: 0,
            boxShadow: `0 2px 8px ${accentColor}30`,
          }}
        >
          {getInitials(member.name || member.email)}
        </Avatar>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text strong style={{ fontSize: 15 }}>{member.name || member.email}</Text>
            {emailFetch.fromCache && (
              <Tag
                color="blue"
                style={{
                  borderRadius: 6,
                  fontSize: 11,
                  fontWeight: 600,
                  margin: 0,
                  padding: '0 8px',
                  lineHeight: '22px',
                }}
              >
                Cached
              </Tag>
            )}
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>{member.email}</Text>
        </div>
      </div>

      <ControlsBar
        onAnalyze={(from, to) => onAnalyze(member, from, to, !!metrics)}
        onDisconnect={null}
        loading={emailFetch.loading}
        hasData={!!metrics}
        hideDisconnect
        isDark={isDark}
        onRangeChange={(next) => {
          rangeRef.current = next;
          const [from, to] = next || [];
          if (from && to) setRangeDays(Math.max(1, to.diff(from, 'day')));
        }}
      />

      {expired && !emailFetch.loading && (
        <Alert
          type="warning"
          message="Gmail connection expired"
          description="This member’s Gmail grant expired. Ask them to reconnect in Email Insights — do not treat this as Connected."
          showIcon
          style={{ marginBottom: 20, borderRadius: 10 }}
          action={
            <Button size="small" onClick={onBack} style={{ borderRadius: 8 }}>
              Back to team
            </Button>
          }
        />
      )}

      {emailFetch.error && !emailFetch.loading && !expired && (
        <Alert type="error" message="Failed to fetch emails" description={emailFetch.error} showIcon closable style={{ marginBottom: 20, borderRadius: 10 }} />
      )}

      {emailFetch.error && !emailFetch.loading && expired && (
        <Alert type="error" message="Failed to fetch emails" description={emailFetch.error} showIcon style={{ marginBottom: 20, borderRadius: 10 }} />
      )}

      {emailFetch.loading && (
        <FetchProgress fetched={emailFetch.progress.fetched} total={emailFetch.progress.total} isDark={isDark} />
      )}

      {!emailFetch.loading && metrics && (
        <>
          {metrics.totalReceived === 0 && metrics.totalSent === 0 ? (
            <Alert type="info" message="No emails found for the selected date range." showIcon style={{ borderRadius: 10, marginBottom: 20 }} />
          ) : (
            <>
              <StatsRow metrics={metrics} isDark={isDark} delayThreshold={delayThreshold} onDelayedClick={() => setDelayedModalOpen(true)} onDebtClick={() => setDebtModalOpen(true)} onLoopsClick={() => setLoopsModalOpen(true)} />
              <Charts metrics={metrics} isDark={isDark} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
                <HeatmapChart metrics={metrics} isDark={isDark} />
                <DayOfWeekChart metrics={metrics} isDark={isDark} />
              </div>
              <ContactTables metrics={metrics} isDark={isDark} />
              <ResponseRatePanel metrics={metrics} isDark={isDark} />
              <CommunicationLoopsPanel metrics={metrics} isDark={isDark} externalOpen={loopsModalOpen} onExternalClose={() => setLoopsModalOpen(false)} />
              <EmailDebtPanel metrics={metrics} isDark={isDark} externalOpen={debtModalOpen} onExternalClose={() => setDebtModalOpen(false)} />
              <DelayedEmailsPanel metrics={metrics} isDark={isDark} onThresholdChange={setDelayThreshold} open={delayedModalOpen} onOpen={() => setDelayedModalOpen(true)} onClose={() => setDelayedModalOpen(false)} />
            </>
          )}
        </>
      )}

      {!emailFetch.loading && !metrics && !emailFetch.error && (
        <ReadyToAnalyze
          isDark={isDark}
          rangeDays={rangeDays}
          onAnalyze={() => {
            const [from, to] = rangeRef.current || [];
            if (!from || !to) return;
            onAnalyze(member, from.toDate(), to.toDate(), false);
          }}
        />
      )}
    </div>
  );
}

/** Main export — rendered as Tab 2 content */
export function TeamInsightsTab({
  team,
  isDark,
  loading,
  page,
  pageSize,
  total,
  totalPages,
  onPageChange,
  search = '',
  setSearch = null,
  // ADDITIVE: org_admin gets My Team | Org-wide sub-tabs
  isOrgAdmin = false,
  orgWide = null, // hook result with search/setSearch | null
}) {
  const emailFetch = useEmailFetch();
  const [selectedMember, setSelectedMember] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [teamState, setTeamState] = useState(team);
  const [subTab, setSubTab] = useState('my'); // 'my' | 'org'
  const [searchInput, setSearchInput] = useState('');
  const [memberConnectionExpired, setMemberConnectionExpired] = useState(false);
  // Persist 410 "reconnect required" on cards — prop sync must not wipe this
  const [expiredUserIds, setExpiredUserIds] = useState(() => new Set());
  const debounceRef = useRef(null);

  const showOrgWide = isOrgAdmin && orgWide;
  const activeList = showOrgWide && subTab === 'org' ? orgWide : null;
  const displayTeam = activeList ? activeList.team : team;
  const displayLoading = activeList ? activeList.loading : loading;
  const displayPage = activeList ? activeList.page : page;
  const displayPageSize = activeList ? activeList.pageSize : pageSize;
  const displayTotal = activeList ? activeList.total : total;
  const displayTotalPages = activeList ? activeList.totalPages : totalPages;
  const displayPageChange = activeList ? activeList.goToPage : onPageChange;
  const displaySearch = activeList ? (activeList.search || '') : (search || '');
  const displaySetSearch = activeList ? activeList.setSearch : setSearch;

  // Keep local input in sync when switching My Team / Org-wide
  useEffect(() => {
    setSearchInput(displaySearch);
  }, [subTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync list from API, but re-apply known expired overlays so cards stay orange
  useEffect(() => {
    setTeamState(
      (displayTeam || []).map((m) => (
        expiredUserIds.has(m.userId)
          ? { ...m, tokenExpired: true, gmailConnected: false, hasRefreshToken: false }
          : m
      ))
    );
  }, [displayTeam, expiredUserIds]);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const markMemberExpired = useCallback((userId) => {
    setExpiredUserIds((prev) => {
      if (prev.has(userId)) return prev;
      const next = new Set(prev);
      next.add(userId);
      return next;
    });
    setTeamState((prev) =>
      prev.map((m) => (
        m.userId === userId
          ? { ...m, tokenExpired: true, gmailConnected: false, hasRefreshToken: false }
          : m
      ))
    );
  }, []);

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchInput(value);
    if (!displaySetSearch) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      displaySetSearch(value);
    }, 300);
  }, [displaySetSearch]);

  const handleSearchClear = useCallback(() => {
    setSearchInput('');
    if (displaySetSearch) displaySetSearch('');
  }, [displaySetSearch]);

  const handleViewInsights = useCallback((member) => {
    setFetchError(null);
    setMemberConnectionExpired(false);
    setSelectedMember(member);
    emailFetch.reset();
  }, [emailFetch]);

  const handleAnalyzeMember = useCallback(async (member, from, to, forceRefresh = false) => {
    const res = await emailFetch.fetchForMember(member.userId, from, to, forceRefresh);
    if (res?.tokenExpired) {
      // Card must show Expired (not green Connected) after reconnect-required error
      setMemberConnectionExpired(true);
      markMemberExpired(member.userId);
    }
  }, [emailFetch, markMemberExpired]);

  const handleBack = useCallback(() => {
    setSelectedMember(null);
    setMemberConnectionExpired(false);
    emailFetch.reset();
  }, [emailFetch]);

  const handleSubTab = useCallback((key) => {
    setSubTab(key);
    setSelectedMember(null);
    setMemberConnectionExpired(false);
    emailFetch.reset();
  }, [emailFetch]);

  if (selectedMember) {
    return (
      <MemberInsightsView
        member={{
          ...selectedMember,
          ...(memberConnectionExpired
            ? { tokenExpired: true, gmailConnected: false }
            : {}),
        }}
        emailFetch={emailFetch}
        isDark={isDark}
        onBack={handleBack}
        onAnalyze={handleAnalyzeMember}
        connectionExpired={memberConnectionExpired}
      />
    );
  }

  const hasSearch = !!(searchInput || displaySearch).trim();
  const emptyMessage = hasSearch
    ? 'No members match your search.'
    : (subTab === 'org'
      ? 'No other members found in your organization.'
      : 'No reportees found in your team.');

  if (!displayLoading && displayTeam.length === 0 && !showOrgWide && !hasSearch && !displaySetSearch) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <Users size={48} color="#9ca3af" style={{ marginBottom: 16 }} />
        <Text type="secondary" style={{ fontSize: 15, display: 'block' }}>{emptyMessage}</Text>
      </div>
    );
  }

  const connectedOnPage = teamState.filter((m) => m.gmailConnected).length;
  const title = subTab === 'org' ? 'Organization' : 'Your Team';
  const subtitle = hasSearch
    ? `${displayTotal} match${displayTotal === 1 ? '' : 'es'} for “${(searchInput || displaySearch).trim()}”`
    : (subTab === 'org'
      ? `${connectedOnPage} connected on this page · ${displayTotal} members org-wide`
      : `${connectedOnPage} of ${displayTotal} members have Gmail connected`);

  return (
    <div>
      {showOrgWide && (
        <div
          style={{
            display: 'inline-flex',
            gap: 4,
            padding: 4,
            marginBottom: 16,
            borderRadius: 10,
            background: isDark ? 'rgba(255,255,255,0.04)' : '#f3f4f6',
          }}
        >
          {[
            { key: 'my', label: 'My Team', count: total },
            { key: 'org', label: 'Org-wide', count: orgWide.total },
          ].map((t) => {
            const active = subTab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => handleSubTab(t.key)}
                style={{
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: 8,
                  padding: '6px 14px',
                  fontSize: 13,
                  fontWeight: 600,
                  background: active
                    ? (isDark ? 'rgba(99,102,241,0.35)' : '#ffffff')
                    : 'transparent',
                  color: active
                    ? (isDark ? '#e0e7ff' : '#4338ca')
                    : (isDark ? '#94a3b8' : '#6b7280'),
                  boxShadow: active && !isDark ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                }}
              >
                {t.label}
                {t.key === 'my' && !loading ? ` (${t.count})` : ''}
                {t.key === 'org' && !orgWide.loading ? ` (${t.count})` : ''}
              </button>
            );
          })}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 20,
        }}
      >
        <div>
          <Title level={5} style={{ margin: 0 }}>{title}</Title>
          <Text type="secondary" style={{ fontSize: 13 }}>{subtitle}</Text>
        </div>
        {displaySetSearch && (
          <Input
            allowClear
            value={searchInput}
            onChange={handleSearchChange}
            onClear={handleSearchClear}
            placeholder="Search by name or email…"
            prefix={<Search size={14} color={isDark ? '#94a3b8' : '#9ca3af'} />}
            style={{
              maxWidth: 320,
              width: '100%',
              borderRadius: 10,
              height: 36,
            }}
          />
        )}
      </div>

      {fetchError && (
        <Alert type="warning" message={fetchError} showIcon closable onClose={() => setFetchError(null)} style={{ marginBottom: 16, borderRadius: 10 }} />
      )}

      {!displayLoading && displayTeam.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <Users size={40} color="#9ca3af" style={{ marginBottom: 12 }} />
          <Text type="secondary" style={{ fontSize: 14, display: 'block' }}>{emptyMessage}</Text>
        </div>
      ) : (
        <div style={{ opacity: displayLoading ? 0.45 : 1, pointerEvents: displayLoading ? 'none' : 'auto', transition: 'opacity 0.25s ease' }}>
          <Row gutter={[16, 16]} align="stretch">
            {[...teamState].sort((a, b) => {
              const rank = (m) => (m.gmailConnected ? 2 : m.tokenExpired ? 1 : 0);
              const d = rank(b) - rank(a);
              if (d !== 0) return d;
              return (a.name || '').localeCompare(b.name || '');
            }).map((member) => (
              <Col key={member.userId} xs={24} sm={12} md={8} lg={6} style={{ display: 'flex' }}>
                <MemberCard
                  member={member}
                  isDark={isDark}
                  loading={false}
                  onViewInsights={handleViewInsights}
                />
              </Col>
            ))}
          </Row>
        </div>
      )}

      {displayTotalPages > 1 && (
        <div className="email-insights-pagination" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
          <Pagination
            current={displayPage}
            total={displayTotal}
            pageSize={displayPageSize}
            onChange={(p, size) => displayPageChange(p, size)}
            onShowSizeChange={(current, size) => displayPageChange(1, size)}
            showSizeChanger
            pageSizeOptions={['12', '24', '48']}
            disabled={displayLoading}
          />
        </div>
      )}
    </div>
  );
}
